import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { batchLabel } from '../hooks/useBatches';
import { useIsMobile } from '../hooks/useMobile';

/** /nominator/quota 后端返回结构（一行 = 一个部门指派）。 */
type QuotaRow = {
  deptId: number | null;
  deptName: string | null;
  headcount: number;
  limit: number | null;
  used: number;
  remaining: number | null;
};

/**
 * 晋升提名（领导端）。
 *
 * 2026-04 改造要点：
 *  - 表单精简到 3 项：被提名人 / 晋升职级 / 晋升理由。
 *  - HR 在「提名领导指定」页保存指派后，领导登录即可填写——
 *    系统不再走任何推送（钉钉 / 站内）通道，通知由 HR 线下自行安排。
 *  - 提交隐式默认 suggestion = 'HR_DECIDE'，deptPriority 由后端按提交顺序自动赋值。
 */
/** 表中一行提名（后端 listMyNominations 返回结构）。 */
type NominationRow = {
  id: number;
  batchId: number;
  nomineeId: number;
  targetRank: string | null;
  highlight: string;
  submitAt: string | null;
  nominee: {
    id: number;
    name: string;
    position: string | null;
    deptPath: string | null;
  };
};

export function NominatorPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [batchId, setBatchId] = useState<number | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  /** 编辑模式：null = 新建；number = 该提名的 id。 */
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const { data: batches = [] } = useQuery<any[]>({
    queryKey: ['batches-current'],
    queryFn: () => api.get('/batches'),
  });

  // 默认批次：当前 ONGOING 非 sandbox > CALCULATING > 最新一条；保持与 Layout 顶部 Tag 一致。
  // 用户手动改过 batchId 后不再覆盖（保留用户选择）。
  useEffect(() => {
    if (batchId !== undefined) return;
    if (!batches.length) return;
    const pick =
      batches.find((b) => b.phase === 'ONGOING' && b.sandbox !== true) ??
      batches.find((b) => b.phase === 'CALCULATING') ??
      batches[0];
    if (pick) setBatchId(pick.id);
  }, [batches, batchId]);

  const { data: quotas = [] } = useQuery<QuotaRow[]>({
    queryKey: ['nomination-quota', batchId],
    queryFn: () => api.get('/nominator/quota', { params: { batchId } }),
    enabled: !!batchId,
  });

  const { data: nominations = [] } = useQuery({
    queryKey: ['my-nominations', batchId],
    queryFn: () => api.get('/nominator/nominations', { params: { batchId } }),
    enabled: !!batchId,
  });

  // V1.3：提名领导端走 /nominator/candidates（NOMINATOR 角色可访问）。
  // 旧版用的 /admin/employees 仅 ADMIN/SUPER_ADMIN，NOMINATOR 一打开会 403。
  const { data: emps = [] } = useQuery<
    Array<{ id: number; name: string; position: string | null; deptPath: string | null }>
  >({
    queryKey: ['nominator-candidates'],
    queryFn: () => api.get('/nominator/candidates'),
  });

  const createMutation = useMutation({
    mutationFn: (vals: any) =>
      api.post('/nominator/nominations', { batchId, ...vals }),
    onSuccess: () => {
      message.success('已提交提名');
      closeModal();
      qc.invalidateQueries({ queryKey: ['my-nominations'] });
      qc.invalidateQueries({ queryKey: ['nomination-quota'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '提交失败'),
  });

  // 编辑：调 PATCH /nominator/nominations/:id；只能改 targetRank + highlight，
  // 被提名人不允许换（业务上换人就是另一条提名，应该撤回再新建）。
  const updateMutation = useMutation({
    mutationFn: ({ id, vals }: { id: number; vals: { targetRank: string; highlight: string } }) =>
      api.patch(`/nominator/nominations/${id}`, vals),
    onSuccess: () => {
      message.success('已更新提名');
      closeModal();
      qc.invalidateQueries({ queryKey: ['my-nominations'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '更新失败'),
  });

  // 删除：调 DELETE /nominator/nominations/:id；后端会校验 batch.phase=ONGOING，
  // CALCULATING / CLOSED 阶段一律 400「批次已锁定，不可撤回」。
  const removeMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/nominator/nominations/${id}`),
    onSuccess: () => {
      message.success('已撤回提名');
      qc.invalidateQueries({ queryKey: ['my-nominations'] });
      qc.invalidateQueries({ queryKey: ['nomination-quota'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '撤回失败'),
  });

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    form.resetFields();
  }

  function openCreate() {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  }

  function openEdit(row: NominationRow) {
    setEditingId(row.id);
    form.setFieldsValue({
      nomineeId: row.nomineeId,
      targetRank: row.targetRank ?? '',
      highlight: row.highlight ?? '',
    });
    setModalOpen(true);
  }

  /** 没有 quota 行 = HR 还没指派该领导；有任意行即视为已下发，可填写。 */
  const assigned = quotas.length > 0;

  const currentBatchName = useMemo(
    () => batchLabel((batches as any[]).find((b) => b.id === batchId)),
    [batches, batchId],
  );

  /** 顶部状态条：未指派 / 已指派可填。 */
  const statusBanner = useMemo(() => {
    if (!batchId) return null;
    if (!assigned) {
      return (
        <Alert
          type="info"
          message={`本批次（${currentBatchName}）您暂未被指派为提名领导`}
          description="如有问题请联系 HR 在「提名领导指定」页设置。"
          showIcon
          style={{ marginBottom: 16 }}
        />
      );
    }
    return (
      <Alert
        type="success"
        message={`HR 已下发本期晋升提名任务：${currentBatchName} · ${quotas.length} 个部门`}
        description="请按下方各部门额度提交「被提名人 / 晋升职级 / 晋升理由」三项即可，其余排序与建议由 HR 与合议会统一拟定。"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }, [assigned, batchId, currentBatchName, quotas.length]);

  return (
    <div>
      <div className="tg-page-title">晋升提名</div>
      {statusBanner}

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="选择批次"
            style={{ width: 200 }}
            options={(batches as any[]).map((b) => ({ label: batchLabel(b), value: b.id }))}
            onChange={setBatchId}
          />
          <Button
            type="primary"
            onClick={openCreate}
            disabled={!batchId || !assigned}
          >
            新建提名
          </Button>
          {!batchId ? null : !assigned ? (
            <Tag color="default">未被指派</Tag>
          ) : (
            <Tag color="success">可填写</Tag>
          )}
        </Space>

        <Card title="本期配额（按部门）" type="inner" style={{ marginBottom: 16 }}>
          {quotas.length === 0 && (
            <Typography.Text type="secondary">暂无指派</Typography.Text>
          )}
          {quotas.map((q, idx) => (
            <div key={`${q.deptId ?? 'txt'}-${idx}`} style={{ marginBottom: 8 }}>
              <strong>{q.deptName ?? '—'}</strong>
              {q.deptId != null && (
                <Typography.Text type="secondary" style={{ marginLeft: 6 }}>
                  在职 {q.headcount} 人
                </Typography.Text>
              )}
              <Progress
                percent={
                  q.limit != null && q.limit > 0
                    ? Math.min(100, Math.round((q.used / q.limit) * 100))
                    : 0
                }
                format={() =>
                  q.limit == null ? `${q.used} / 不限` : `${q.used} / ${q.limit}`
                }
                style={{ width: 280, marginLeft: 12 }}
              />
              {q.limit == null && (
                <Tag color="default" style={{ marginLeft: 8 }}>
                  未配置上限
                </Tag>
              )}
            </div>
          ))}
        </Card>

        {isMobile ? (
          <List<NominationRow>
            dataSource={nominations as NominationRow[]}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="本期暂未提交任何提名"
                />
              ),
            }}
            split={false}
            pagination={
              (nominations as NominationRow[]).length > 6
                ? { pageSize: 6, showTotal: (n) => `共 ${n} 项` }
                : false
            }
            renderItem={(row) => {
              const dept = row.nominee?.deptPath?.split('/').slice(-2).join(' / ') ?? '—';
              const submitTime = row.submitAt
                ? new Date(row.submitAt).toLocaleString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })
                : '—';
              return (
                <List.Item style={{ padding: '6px 0', borderBottom: 'none' }}>
                  <Card
                    size="small"
                    style={{
                      width: '100%',
                      borderRadius: 12,
                      border: '1px solid rgba(124,92,191,0.15)',
                      boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
                    }}
                    styles={{ body: { padding: 14 } }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 8,
                        gap: 8,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: '#1f1f1f',
                            lineHeight: 1.3,
                          }}
                        >
                          {row.nominee?.name ?? '—'}
                        </div>
                        <div
                          style={{
                            color: '#94a3b8',
                            fontSize: 12,
                            marginTop: 4,
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                          }}
                        >
                          {dept} · {row.nominee?.position ?? '—'}
                        </div>
                      </div>
                      <Tag color="purple" style={{ marginRight: 0, flexShrink: 0 }}>
                        {row.targetRank || '未填职级'}
                      </Tag>
                    </div>
                    <div
                      style={{
                        background: '#faf8ff',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 13,
                        color: '#475569',
                        lineHeight: 1.6,
                        marginBottom: 10,
                        wordBreak: 'break-word',
                      }}
                    >
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                        晋升理由
                      </div>
                      {row.highlight || '—'}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#94a3b8',
                        marginBottom: 10,
                      }}
                    >
                      提交时间：{submitTime}
                    </div>
                    <Space style={{ width: '100%' }} size={8} wrap>
                      <Button size="middle" onClick={() => openEdit(row)} style={{ flex: 1, minWidth: 100 }}>
                        修改
                      </Button>
                      <Popconfirm
                        title="撤回该条提名？"
                        description="撤回后名额释放，可重新提名其他同事。"
                        okText="确认撤回"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => removeMutation.mutate(row.id)}
                      >
                        <Button size="middle" danger style={{ flex: 1, minWidth: 100 }}>
                          撤回
                        </Button>
                      </Popconfirm>
                    </Space>
                  </Card>
                </List.Item>
              );
            }}
          />
        ) : (
          <Table<NominationRow>
            rowKey="id"
            dataSource={nominations as NominationRow[]}
            columns={[
              { title: '被提名人', dataIndex: ['nominee', 'name'], width: 120 },
              { title: '部门', dataIndex: ['nominee', 'deptPath'], ellipsis: true },
              { title: '晋升职级', dataIndex: 'targetRank', width: 140 },
              { title: '晋升理由', dataIndex: 'highlight', ellipsis: true },
              {
                title: '提交时间',
                dataIndex: 'submitAt',
                width: 170,
                render: (t: string | null) =>
                  t ? new Date(t).toLocaleString('zh-CN', { hour12: false }) : '-',
              },
              {
                title: '操作',
                key: 'op',
                width: 140,
                align: 'center',
                fixed: 'right',
                render: (_, row) => (
                  <Space size={4}>
                    <Button type="link" size="small" onClick={() => openEdit(row)}>
                      修改
                    </Button>
                    <Popconfirm
                      title="撤回该条提名？"
                      description="撤回后名额释放，可重新提名其他同事。"
                      okText="确认撤回"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => removeMutation.mutate(row.id)}
                    >
                      <Button type="link" size="small" danger>
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        title={editingId == null ? '新建晋升提名' : '修改晋升提名'}
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={isMobile ? '100vw' : 680}
        className={isMobile ? 'tg-fullscreen-modal' : undefined}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => {
            const targetRank = (v.targetRank ?? '').trim();
            const highlight = (v.highlight ?? '').trim();
            if (editingId != null) {
              // 编辑只允许改 targetRank + highlight；被提名人换人 = 撤回再新建。
              updateMutation.mutate({ id: editingId, vals: { targetRank, highlight } });
            } else {
              // suggestion / deptPriority 不再让领导填，后端会兜底；
              // detailDesc 也精简掉，所有要表达的内容合并在「晋升理由」里。
              createMutation.mutate({ nomineeId: v.nomineeId, targetRank, highlight });
            }
          }}
        >
          <Form.Item
            label="提名晋升人员"
            name="nomineeId"
            rules={[{ required: true, message: '请选择被提名人' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="按姓名 / 职位 / 部门搜索"
              disabled={editingId != null}
              options={emps.map((e) => ({
                label: `${e.name} · ${e.position ?? '-'} · ${e.deptPath ?? '-'}`,
                value: e.id,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="晋升职级"
            name="targetRank"
            extra="拟晋升到的具体职级，例如「主管」「P6」「高级专员」"
            rules={[
              { required: true, message: '请填写晋升职级' },
              { whitespace: true, message: '不能为空白' },
              { max: 50, message: '不超过 50 字' },
            ]}
          >
            <Input placeholder="例如：主管 / P6 / 高级专员" maxLength={50} />
          </Form.Item>
          <Form.Item
            label="晋升理由"
            name="highlight"
            extra="近半年关键贡献、突破型事例、量化成果，建议 ≥ 30 字"
            rules={[
              { required: true, message: '请填写晋升理由' },
              { min: 30, message: '请填写 ≥ 30 字的晋升理由' },
            ]}
          >
            <Input.TextArea
              rows={6}
              placeholder="例：负责 XX 项目期间，将客户回款周期从 60 天缩短到 35 天，2026Q1 帮公司提前回笼现金 1200 万。"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
